/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { combineLatest, Observable } from 'rxjs';
import { Component, OnInit } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { AuthState } from '../state/authState.state';
import { Actions, Select, Store } from '@ngxs/store';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { Navigate } from '@ngxs/router-plugin';
import { AppConfigState } from '../../state/appConfig/app-config.state';
import { ActivatedRoute, Router } from '@angular/router';
import { concatMapTo, filter, map, take } from 'rxjs/operators';
import { DisclaimerSeen } from '../state/authState.actions';
import { DashGisUtil } from '../../../modules/shared/dash-gis-util';

/**
 * Acts as a director for the login process.
 * Evaluates current application state and redirects based on
 *  - config value enableDisclaimer
 *  - has the user viewed the disclaimer this session?
 *  - config value enableRemember
 *  - config value autologin
 *  - is the user currently authenticated?
 */
@Component({
    template: `
        <div fxFlexFill class='auth-component login-page'>
            <router-outlet></router-outlet>
        </div>`,
    styleUrls: [ 'authentication.component.scss' ]
})
export class AuthenticationComponent implements OnInit {
    @Select(AppConfigState.loaded) public appConfigLoaded$: Observable<boolean>;
    @Select(AppConfigState.enableDisclaimer) public enableDisclaimer$: Observable<boolean>;
    @Select(AuthState.needsDisclaimer) public needsDisclaimer$: Observable<boolean>;
    @Select(AuthState.excludedRoutes) public excludedRoutes$: Observable<string[]>;

    constructor(
        private logger: LoggerService,
        private authService: AuthService,
        private router: Router,
        private route: ActivatedRoute,
        private actions$: Actions,
        private store: Store
    ) {}

    public ngOnInit(): void {
        // this.actions$.pipe(ofActionSuccessful(AppConfigLoaded)).subscribe(() => {
        //     this.authService.refreshToken();
        // });
        const fullPath = DashGisUtil.buildPath(this.route.snapshot);
        const shouldShowDisclaimer$ = combineLatest([
            this.enableDisclaimer$,
            this.needsDisclaimer$,
            this.excludedRoutes$,
        ]).pipe(
            map(([
                enabled,
                needed,
                excludedRoutes
            ]) => enabled && needed && !excludedRoutes.includes(fullPath))
        );

        this.appConfigLoaded$
            .pipe(
                filter(Boolean),
                concatMapTo(shouldShowDisclaimer$),
                take(1),
            )
            .subscribe((shouldShowDisclaimer) => {
                if (shouldShowDisclaimer) {
                    this.store.dispatch(new Navigate(
                        [ '/auth/disclaimer' ],
                        null,
                        { queryParamsHandling: 'preserve' }
                    ));
                } else {
                    this.store.dispatch(new DisclaimerSeen());
                }
            });
    }
}
